import { useEffect, useState } from 'react';

import { XIcon, Search } from 'lucide-react';
import { DefaultPopover, ContextContent, ContextItem, InputGroup, Input, InputGroupText } from 'twyne-new.ui';

const TitleSandbox = ({ children, className = '' }) => <div className={`text-black dark:text-white text-xs font-semibold ${className}`}>{children}</div>

const SeparatorSandbox = () => <div className='border border-gray-300 dark:border-gray-800 h-[1px] w-full my-2 mr-2' />

const ListContainer = ({ children, className = '', onCloseContextMenu }) => <div className={`${className} overflow-y-auto overflow-x-hidden`} onScroll={() => onCloseContextMenu()}>{children}</div>

const ContextMenuPopover = ({ selected, options = [], className, mouse }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(!!selected)
  }, [selected])

  const handleClick = (e, onClick, closeOnClick = true) => {
    e.preventDefault()
    e.stopPropagation()
    onClick(e)
    if (closeOnClick) {
      setOpen(false)
    }
  }

  if (!selected) {
    return null
  }

  return <DefaultPopover
    trigger={<div className={`pointer-events-none fixed z-50 w-5 h-5 ${className}`} style={{ left: mouse.left, top: mouse.top }} />}
    triggerProps={{ asChild: true }}
    contentProps={{ side: 'bottom', align: 'start', className: 'w-fit p-0' }}
    wrapperProps={{ open, onOpenChange: setOpen }}
  >
    <ContextContent>
      {options.map(({ label, onClick, component, disabled, closeOnClick }) => {
        if (component) {
          return component
        }

        return <ContextItem key={label} onClick={e => handleClick(e, onClick, closeOnClick)} disabled={disabled}>
          {label}
        </ContextItem>
      })}
    </ContextContent>
  </DefaultPopover>
}

const Subtitle = ({ subtitle, className = '' }) => <span className={`text-gray-900 dark:text-gray-300 text-xs ${className}`}>
  {subtitle}
</span>

const SearchInput = ({ title, value, onChange, onClose }) => <>
  <div className='flex justify-between items-center mb-2'>
    <div className="flex gap-2 items-center">
      <div className='twy-text-base-accent text-[14px]'>{title}</div>
      <InputGroup variant='accent' className='h-6'>
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          size='sm'
          variant='accent'
        />
        <InputGroupText addonType='append'>
          <Search size='14' />
        </InputGroupText>
      </InputGroup>
    </div>
    <XIcon size='16' className='cursor-pointer' onClick={onClose} />
  </div>
</>

function searchItems({ query, defaultItems, keysToSearch = [] }) {
  if (!query) return defaultItems

  const lowerQuery = query.toLowerCase()

  return defaultItems.map(groupObj => {
    const matchedItems = groupObj.items.filter(item => keysToSearch.some(key => item[key]?.toLowerCase().includes(lowerQuery)))

    return {
      category: groupObj.category,
      items: matchedItems,
    }
  }).filter(groupObj => groupObj.items.length > 0)
}

const ButtonSandbox = ({ children, variant = 'default', size = 'md', color = 'neutral', block = false, active = false, className, ...props }) => {
  const variants = {
    default: 'text-white',
    light: '',
    ghost: 'bg-transparent',
    outline: 'bg-transparent border',
  }

  const sizes = {
    sm: 'py-1 px-2',
    md: 'py-2 px-3',
    lg: 'py-3 px-4 text-[14px]',
  }

  const blocks = {
    true: 'w-full justify-center',
    false: '',
  }

  const compoundVariants = [{
    variant: 'default', color: 'neutral',
    class: ['twy-bg-neutral', 'twy-bg-neutral-hover'],
  }, {
    variant: 'default', color: 'accent',
    class: ['twy-bg-accent', 'twy-bg-accent-hover'],
  }, {
    variant: 'default', color: 'success',
    class: ['twy-bg-success', 'twy-bg-success-hover'],
  }, {
    variant: 'default', color: 'warning',
    class: ['twy-bg-warning', 'twy-bg-warning-hover'],
  }, {
    variant: 'default', color: 'danger',
    class: ['twy-bg-danger', 'twy-bg-danger-hover'],
  }, {
    variant: 'light', color: 'neutral',
    class: ['twy-subtle-neutral', 'twy-text-neutral', 'twy-subtle-neutral-hover'],
  }, {
    variant: 'light', color: 'accent',
    class: ['twy-subtle-accent', 'twy-text-accent', 'twy-subtle-accent-hover'],
  }, {
    variant: 'light', color: 'success',
    class: ['twy-subtle-success', 'twy-text-success', 'twy-subtle-success-hover'],
  }, {
    variant: 'light', color: 'warning',
    class: ['twy-subtle-warning', 'twy-text-warning', 'twy-subtle-warning-hover'],
  }, {
    variant: 'light', color: 'danger',
    class: ['twy-subtle-danger', 'twy-text-danger', 'twy-subtle-danger-hover'],
  }, {
    variant: 'ghost', color: 'neutral',
    class: ['twy-text-neutral', 'hover:twy-subtle-neutral'],
  }, {
    variant: 'ghost', color: 'accent',
    class: ['twy-text-accent', 'hover:twy-subtle-accent'],
  }, {
    variant: 'ghost', color: 'success',
    class: ['twy-text-success', 'hover:twy-subtle-success'],
  }, {
    variant: 'ghost', color: 'warning',
    class: ['twy-text-warning', 'hover:twy-subtle-warning'],
  }, {
    variant: 'ghost', color: 'danger',
    class: ['twy-text-danger', 'hover:twy-subtle-danger'],
  }, {
    variant: 'outline', color: 'neutral',
    class: ['twy-border-neutral', 'twy-text-neutral', 'hover:twy-subtle-neutral'],
  }, {
    variant: 'outline', color: 'neutral', active: true,
    class: ['outline', 'outline-2', 'outline-gray-600'],
  }, {
    variant: 'outline', color: 'accent',
    class: ['twy-border-accent', 'twy-text-accent', 'hover:twy-subtle-accent'],
  }, {
    variant: 'outline', color: 'accent', active: true,
    class: ['outline', 'outline-2', 'outline-blue-400'],
  }, {
    variant: 'outline', color: 'success',
    class: ['twy-border-success', 'twy-text-success', 'hover:twy-subtle-success'],
  }, {
    variant: 'outline', color: 'success', active: true,
    class: ['outline', 'outline-2', 'outline-green-400'],
  }, {
    variant: 'outline', color: 'warning',
    class: ['twy-border-warning', 'twy-text-warning', 'hover:twy-subtle-warning'],
  }, {
    variant: 'outline', color: 'warning', active: true,
    class: ['outline', 'outline-2', 'outline-yellow-400'],
  }, {
    variant: 'outline', color: 'danger',
    class: ['twy-border-danger', 'twy-text-danger', 'hover:twy-subtle-danger'],
  }, {
    variant: 'outline', color: 'danger', active: true,
    class: ['outline', 'outline-2', 'outline-red-400'],
  }]

  const defaultClasses = 'cursor-pointer button rounded-xs transition-colors inline-flex items-center gap-1 whitespace-nowrap disabled:pointer-events-none disabled:opacity-50'
  const compoundClasses = compoundVariants.filter(item => item.variant === variant && item.color === color && (active ? true : !item.active)).map(item => item.class.join(' ')).join(' ')
  const classes = `${defaultClasses} ${variants[variant]} ${sizes[size]} ${blocks[block]} ${compoundClasses} ${className}`

  return <>
    <button className={classes} {...props}>
      {children}
    </button>
  </>
}

export {
  TitleSandbox,
  SeparatorSandbox,
  ListContainer,
  ContextMenuPopover,
  SearchInput,
  Subtitle,
  searchItems,
  ButtonSandbox,
}

